import { useCallback, useMemo, useState } from "react";
import { SavedReport } from "@/entities/savedReports/types";
import { useOpenReport } from "@/components/global/Chat/components/dreamerReports/useOpenReport";

/**
 * What a surface showing a list of reports has to hold: the report a row asked
 * to open, and the report behind each of the row's modals. Opening a report
 * also generates it; what the run is doing comes from the report runs context.
 *
 * The panel and the expanded table both show the same rows with the same
 * actions, and neither can own these modals from the row itself — every one of
 * these mutations can drop the row that raised it, and a row cannot outlive its
 * own mutation. Kept here so a new row action is wired once rather than twice.
 */
export const useReportSurfaceTargets = () => {
  const [openedReport, setOpenedReport] = useState<SavedReport | null>(
    null
  );
  const [scheduleTarget, setScheduleTarget] = useState<SavedReport | null>(
    null
  );
  const [removeScheduleTarget, setRemoveScheduleTarget] =
    useState<SavedReport | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SavedReport | null>(
    null
  );

  const openReport = useOpenReport(setOpenedReport);

  const rowHandlers = useMemo(
    () => ({
      onOpen: openReport,
      onSchedule: (report: SavedReport) => setScheduleTarget(report),
      onRemoveSchedule: (report: SavedReport) =>
        setRemoveScheduleTarget(report),
      onDelete: (report: SavedReport) => setDeleteTarget(report)
    }),
    [openReport]
  );

  const closeOpenedReport = useCallback(() => setOpenedReport(null), []);
  const closeSchedule = useCallback(() => setScheduleTarget(null), []);
  const closeRemoveSchedule = useCallback(
    () => setRemoveScheduleTarget(null),
    []
  );
  const closeDelete = useCallback(() => setDeleteTarget(null), []);

  const previewHostProps = useMemo(
    () => ({ report: openedReport, onClose: closeOpenedReport }),
    [openedReport, closeOpenedReport]
  );

  const rowModalsProps = useMemo(
    () => ({
      scheduleTarget,
      removeScheduleTarget,
      deleteTarget,
      onCloseSchedule: closeSchedule,
      onCloseRemoveSchedule: closeRemoveSchedule,
      onCloseDelete: closeDelete
    }),
    [
      scheduleTarget,
      removeScheduleTarget,
      deleteTarget,
      closeSchedule,
      closeRemoveSchedule,
      closeDelete
    ]
  );

  /** For a surface that can itself be closed while a row's modal is open. */
  const clearTargets = useCallback(() => {
    setOpenedReport(null);
    setScheduleTarget(null);
    setRemoveScheduleTarget(null);
    setDeleteTarget(null);
  }, []);

  return { rowHandlers, previewHostProps, rowModalsProps, clearTargets };
};
